import { useCallback, useEffect, useState } from 'react';
import { extractErrorMessage, listDocuments } from '../../api/approval';
import { useApprovalStore } from '../../store/approvalStore';
import { useAuthStore } from '../../store/authStore';
import type { ApprovalDocumentSummary, DocumentView } from '../../types/approval';
import { StatusBadge } from '../../components/StatusBadge';
import { ApprovalDetail } from './ApprovalDetail';
import { tApproval } from '../../lib/approval-i18n';
import './ApprovalPage.css';

/**
 * 문서함 — **목록 + 상세** 두 열. 함이 셋이고 화면은 하나다 (2026-08-31 · 시안 F1).
 *
 * ★★ 종전에는 `ApprovalPage` 한 화면이 사이드바 · 문서함 · 관리 화면 넷을 **전부** 들고 있어,
 *    무엇을 고쳐도 나머지가 함께 흔들렸다. 셸(`ApprShell`)이 트리를 가져가면서 이 화면에는
 *    **함 하나를 그리는 일**만 남았다 — 어느 함인지는 라우트가 정한다(`view`).
 * ★ 제목을 라우트가 준다 — **메뉴 이름과 같은 글자**여야 한다(`appr-menu.test.ts` 가 못 박는다).
 * ★ 「새 결재 올리기」 단추는 여기 없다. 셸의 트리 머리에 하나뿐이다 — 종전에는 화면 머리마다
 *   붙어, 관리 화면에서도 기안 단추가 떴다.
 */
interface Props {
  view: DocumentView;
  /** 화면 제목 = 메뉴 이름. */
  title: string;
}

export function ApprovalDocsPage({ view, title }: Props): JSX.Element {
  const userId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  // SSE 실시간 갱신 — TopBar 의 useSSE 가 onApprovalStatus → store bump.
  const lastApprovalBump = useApprovalStore((s) => s.lastApprovalStatusBump);
  const [items, setItems] = useState<ApprovalDocumentSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const refetch = useCallback(async (v: DocumentView) => {
    setLoading(true);
    setError(null);
    try {
      const res = await listDocuments(v);
      setItems(res.items);
    } catch (err) {
      setError(extractErrorMessage(err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 함이 바뀌면 고른 문서도 놓는다 — 다른 함의 문서가 상세에 남아 있으면 안 된다.
  useEffect(() => {
    setSelectedId(null);
    void refetch(view);
  }, [view, refetch]);

  useEffect(() => {
    if (lastApprovalBump === 0) return;
    void refetch(view);
  }, [lastApprovalBump, view, refetch]);

  return (
    <div className="approval-page__main aprdocs">
      <header className="approval-page__header">
        <h1>{title}</h1>
      </header>

      <div className="approval-page__body">
        <section className="approval-page__list" aria-label={tApproval('approval.list.aria_label')}>
          {loading && <p className="approval-page__hint">{tApproval('approval.loading')}</p>}
          {error && <p className="approval-page__error">{error}</p>}
          {!loading && !error && items.length === 0 && (
            <p className="approval-page__hint">{tApproval('approval.empty')}</p>
          )}
          {!loading && !error && items.map((it) => (
            <button
              key={it.id}
              type="button"
              className={`approval-page__item ${selectedId === it.id ? 'is-selected' : ''}`}
              onClick={() => setSelectedId(it.id)}
            >
              <div className="approval-page__item-title">
                {it.isUrgent && (
                  <span className="approval-page__urgent-badge">{tApproval('approval.urgent_badge')}</span>
                )}
                {it.title}
              </div>
              <div className="approval-page__item-meta">
                <span>{it.type}</span>
                <span>·</span>
                <span>{it.currentStep}/{it.totalSteps}{tApproval('approval.steps_suffix')}</span>
                <span>·</span>
                <StatusBadge status={it.status} />
              </div>
            </button>
          ))}
        </section>

        <section className="approval-page__detail" aria-label="결재 상세">
          {selectedId ? (
            <ApprovalDetail
              key={selectedId}
              documentId={selectedId}
              currentUserId={userId}
              onChanged={() => refetch(view)}
            />
          ) : (
            <div className="approval-page__detail-empty">
              왼쪽 목록에서 결재 문서를 선택하세요.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
